/**
 * PATH: packages/ui/src/kanban/components/card-detail/resolveCardDrawerChrome.ts
 * FILE_ID: UI-KANBAN-DRAWER-CHROME-001
 * PURPOSE: Pure I→O for CardDetailDrawer wrapper/aside classes and whether
 *   the shell must escape to the viewport (portal).
 * WHY: Job Hub's tab body is overflow-hidden. The phone inline sheet used
 *   `absolute … max-h-[80vh]` inside that clip, so the card popup lost its
 *   top (title / close). md+ stays a flex sibling (2026-06-16 split-pane).
 * FLOW:
 *   ┌─────────────┐   ┌──────────────┐   ┌─────────────────────┐
 *   │ mode flags  │ → │ pick chrome  │ → │ class + portal flag │
 *   └─────────────┘   └──────────────┘   └─────────────────────┘
 * RELATED:
 *   - ./useCardDrawerLayout.ts (consumer)
 *   - ../rightRailPhoneSheet.ts (shared Emails / Jobs / Card geometry)
 */
import {
  RIGHT_RAIL_PHONE_CARD_ASIDE_CLASS,
  RIGHT_RAIL_PHONE_CARD_WRAPPER_CLASS,
} from '../rightRailPhoneSheet'

export interface ResolveCardDrawerChromeArgs {
  popup: boolean
  inlineMode: boolean
  isNarrowViewport: boolean
  pinned: boolean
}

export interface CardDrawerChrome {
  wrapperClass: string
  asideClass: string
  /** Phone + inline: leave the overflow-hidden Job Hub column via portal. */
  escapeToViewport: boolean
}

/** md+ inline rail — flex sibling. Must stay this exact token for split-pane pins. */
export const INLINE_MD_WRAPPER_CLASS =
  'absolute inset-x-0 bottom-0 z-30 flex max-h-[80vh] md:static md:inset-auto md:z-auto md:max-h-none md:shrink-0 min-w-0'

export const INLINE_MD_ASIDE_CLASS =
  'relative flex w-full min-w-0 overflow-hidden flex-col rounded-t-2xl border-t border-neutral-200 bg-white shadow-2xl dark:border-neutral-800 dark:bg-neutral-950 md:h-full md:w-full md:rounded-none md:border-l md:border-t-0 md:shadow-none'

/** Phone + inline — same 92dvh bottom sheet + dim scrim as Emails / Jobs. */
export const INLINE_NARROW_WRAPPER_CLASS = RIGHT_RAIL_PHONE_CARD_WRAPPER_CLASS

export const INLINE_NARROW_ASIDE_CLASS = RIGHT_RAIL_PHONE_CARD_ASIDE_CLASS

const POPUP_WRAPPER_CLASS =
  'fixed inset-0 z-[55] flex items-center justify-center bg-black/40 backdrop-blur-sm p-3 md:p-8'

const POPUP_ASIDE_CLASS =
  'relative flex h-full max-h-[94vh] w-[min(1100px,96vw)] flex-col overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-2xl dark:border-neutral-800 dark:bg-neutral-950 animate-in zoom-in-95 fade-in duration-150'

const PINNED_WRAPPER_CLASS = 'fixed top-0 right-0 bottom-0 z-[40] flex'

const PINNED_ASIDE_CLASS =
  'relative flex h-full w-full flex-col border-l border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-950 shadow-xl'

const OVERLAY_WRAPPER_CLASS =
  'fixed inset-0 z-[55] flex justify-stretch md:justify-end bg-black/30 backdrop-blur-sm'

const OVERLAY_ASIDE_CLASS =
  'relative flex h-full w-full md:w-auto flex-col border-l border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-950 shadow-2xl animate-in slide-in-from-bottom md:slide-in-from-right duration-200'

export function resolveCardDrawerChrome({
  popup,
  inlineMode,
  isNarrowViewport,
  pinned,
}: ResolveCardDrawerChromeArgs): CardDrawerChrome {
  if (popup) {
    return {
      wrapperClass: POPUP_WRAPPER_CLASS,
      asideClass: POPUP_ASIDE_CLASS,
      escapeToViewport: false,
    }
  }
  if (inlineMode && isNarrowViewport) {
    return {
      wrapperClass: INLINE_NARROW_WRAPPER_CLASS,
      asideClass: INLINE_NARROW_ASIDE_CLASS,
      escapeToViewport: true,
    }
  }
  if (inlineMode) {
    return {
      wrapperClass: INLINE_MD_WRAPPER_CLASS,
      asideClass: INLINE_MD_ASIDE_CLASS,
      escapeToViewport: false,
    }
  }
  if (pinned) {
    return {
      wrapperClass: PINNED_WRAPPER_CLASS,
      asideClass: PINNED_ASIDE_CLASS,
      escapeToViewport: false,
    }
  }
  return {
    wrapperClass: OVERLAY_WRAPPER_CLASS,
    asideClass: OVERLAY_ASIDE_CLASS,
    escapeToViewport: false,
  }
}
